import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { isMobile, isIE } from 'react-device-detect';
import { isEmpty } from 'lodash-es';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import { isMobileNotSdkSelector } from 'redux/selectors/global';
import { initTranslations } from 'services/i18n';
import { initAnalytics } from 'services/tracking';
import localStorageService from 'services/localStorage';
import Hosted from 'routes/Hosted';
import {
  getHostedPageType,
  isHostedCardToolkit,
  isHostedPagePreview,
  isIframe
} from 'utilities/global';
import { HOSTED_PAGE_MODES, HOSTED_PAGE_TITLE, HOSTED_PAGE_TYPES_TOKENS } from 'consts';
import { sendParentMessage, listenMessage } from 'services/iframeService';
import { initApi } from 'services/api';
import { isApplePaySupported } from 'services/digitalWalletProvider/applePay';
import { useConfig, useActions } from 'hooks';
import usePreviewLanguage from 'hooks/usePreviewLanguage';

const setSdkStyles = () => {
  document.body.style.background = 'unset';
  const overflow = isIE ? 'scroll' : 'hidden';
  document.body.style.overflow = overflow;
};

const App = () => {
  const location = useLocation();
  const {
    setIsMobile,
    setSdkParams,
    setSessionId,
    updateApplePaySupportedDevice,
    setIsPreview,
    setUserFirstPageUrl
  } = useActions();
  const configData = useConfig(initTranslations, initAnalytics, initApi, setSessionId);
  const isMobileNotSdk = useSelector(isMobileNotSdkSelector);
  const isInIframe = isIframe();
  const isPreview = isHostedPagePreview();
  const {
    token: queryParamToken,
    embedded,
    mobile_view
  } = useMemo(() => Object.fromEntries(new URLSearchParams(location.search)), []);

  const hostedPageType = getHostedPageType();
  const pageToken =
    queryParamToken ||
    localStorageService.getItem(HOSTED_PAGE_TYPES_TOKENS[hostedPageType]);

  const handleMobileViewUserRequestLogic = () => {
    if (!pageToken) return false;

    const parseStringToBoolean = str => str?.toLowerCase() === 'true';

    const mobileViewKey = `${pageToken}_mobile_view_user_request`;
    const savedMobileView = localStorageService.getItem(mobileViewKey);
    // if exists in local storage - case to boolean and return
    if (savedMobileView) {
      return parseStringToBoolean(savedMobileView);
    }

    // if not exists - save in local storage
    const mobileViewBooleanValue = parseStringToBoolean(mobile_view);
    if (mobileViewBooleanValue) {
      localStorageService.setItem(mobileViewKey, mobileViewBooleanValue);
    }

    return mobileViewBooleanValue;
  };

  const initDigitalProviders = () => {
    const isSupportedForApplyPay = isApplePaySupported();
    updateApplePaySupportedDevice({ supported: isSupportedForApplyPay });
  };

  const initPreviewMode = () => {
    setIsPreview(true);
    listenMessage(HOSTED_PAGE_MODES.preview);
  };

  const setFirstPageUrl = () => {
    const {
      location: { href: pageUrl }
    } = window;
    setUserFirstPageUrl(pageUrl);
  };

  useEffect(() => {
    const { referrer } = document;
    if (referrer && referrer !== window.location.href && pageToken) {
      localStorageService.setItem(`${pageToken}_referrer`, referrer);
    }

    const mobileViewUserRequest = handleMobileViewUserRequestLogic();
    if (window.Cypress && window.Cypress.config('viewportWidth') < 500) {
      setIsMobile(true);
    } else {
      setIsMobile(isMobile || mobileViewUserRequest);
    }

    if (isPreview) return initPreviewMode();

    if (isInIframe) {
      setSdkStyles();

      // if used by use toolkit package
      if (isHostedCardToolkit()) {
        setSdkParams({}, true);
        return;
      }

      listenMessage();
      sendParentMessage('GET_PARAMS', null);
      if (isMobile) {
        sendParentMessage('IS_MOBILE', null);
      }

      // if in korta's iframe
      if (embedded) {
        setSdkParams({}, true);
      }
    }

    initDigitalProviders();
    setFirstPageUrl();
  }, []);

  usePreviewLanguage();

  useEffect(() => {
    if (!isMobileNotSdk) return;

    const vh = window.innerHeight * 0.01;
    document.getElementById('root').style.setProperty('--vh', `${vh}px`);
  }, [isMobileNotSdk]);

  if (isEmpty(configData)) return null;

  return (
    <>
      <Helmet>
        <title>{HOSTED_PAGE_TITLE[hostedPageType]}</title>
      </Helmet>
      <Hosted />
    </>
  );
};

export default App;
